// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { OrderModel, OrderSettingsModel } from "@insite/client-framework/Types/ApiModels";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
});

interface OwnProps {
    order: OrderModel;
    orderSettings: OrderSettingsModel;
    extendedStyles?: OrderSummaryCardStyles;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface OrderSummaryCardStyles {
    orderSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    pageTypeLinkGridItem?: GridItemProps;
    orderNumberGridItem?: GridItemProps;
    orderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    webOrderNumberGridItem?: GridItemProps;
    webOrderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    totalGridItem?: GridItemProps;
    totalHeadingAndText?: SmallHeadingAndTextStyles;
    dataTableCells?: DataTableCellBaseProps;
    dataCell?: InjectableCss;
}

export const orderSummaryCardStyles: OrderSummaryCardStyles = {
    orderSummaryCardGridItem: { width: 12 },
    container: { gap: 5 },
    pageTypeLinkGridItem: { width: 12 },
    orderNumberGridItem: { width: 6 },
    statusGridItem: { width: 6 },
    webOrderNumberGridItem: { width: 6 },
    totalGridItem: { width: 6 },
};

const OrderSummaryCard: FC<Props> = ({ language, order, orderSettings, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(orderSummaryCardStyles, extendedStyles));

    const orderDateDisplay = getLocalizedDateTime({
        dateTime: order.orderDate,
        language,
        options: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        },
    });
    const orderNumber = order.erpOrderNumber || order.webOrderNumber;

    return (
        <>
            <DataTableCell
                className="page-type-link-grid-item"
                {...styles.dataCell}
                data-test-selector="orderSummaryCard_Date"
            >
                {orderNumber && <OrderDetailPageTypeLink title={orderDateDisplay} orderNumber={orderNumber} />}
            </DataTableCell>
            <DataTableCell className="order-number-grid-item" {...styles.dataCell}>
                {order.erpOrderNumber && (
                    <SmallHeadingAndText
                        heading=""
                        text={order.erpOrderNumber}
                        extendedStyles={styles.orderNumberHeadingAndText}
                    />
                )}
            </DataTableCell>
            <DataTableCell className="status-grid-item" {...styles.dataCell}>
                {order.statusDisplay && (
                    <SmallHeadingAndText
                        heading=""
                        text={order.statusDisplay}
                        extendedStyles={styles.statusHeadingAndText}
                    />
                )}
            </DataTableCell>
            {orderSettings.showWebOrderNumber && (
                <DataTableCell className="web-order-number-grid-item" {...styles.dataCell}>
                    <SmallHeadingAndText
                        heading=""
                        text={order.webOrderNumber}
                        extendedStyles={styles.webOrderNumberHeadingAndText}
                    />
                </DataTableCell>
            )}
            <DataTableCell className="total-grid-item" {...styles.dataCell}>
                {order.orderGrandTotalDisplay && (
                    <SmallHeadingAndText
                        heading=""
                        text={order.orderGrandTotalDisplay}
                        extendedStyles={styles.totalHeadingAndText}
                    />
                )}
            </DataTableCell>
        </>
    );
};

export default connect(mapStateToProps)(OrderSummaryCard);
